import React, { Suspense } from 'react';
import { Route, Navigate, useParams } from 'react-router-dom';
import { UrlTranslation } from '@/hooks/useUrlTranslations';
import { LazyErrorBoundary } from '@/components/LazyErrorBoundary';
import { STATIC_TRANSLATIONS, PRODUCT_SILOS } from '@/config/urlTranslations';

/** Redirect /zh/blog/:slug → /zh/boke/:slug etc. */
function BlogArticleRedirect({ langPrefix, blogSlug }: { langPrefix: string; blogSlug: string }) {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/${langPrefix}/${blogSlug}/${slug || ''}`} replace />;
}

// Re-export for consumers that already import from here
export { STATIC_TRANSLATIONS, PRODUCT_SILOS } from '@/config/urlTranslations';

// Only Index is eagerly loaded (most common landing page)
import Index from '@/pages/Index';

// Lazy import factories — used for both React.lazy and prefetching
const lazyImports = {
  Shop: () => import('@/pages/Shop'),
  Product: () => import('@/pages/Product'),
  Reviews: () => import('@/pages/Reviews'),
  About: () => import('@/pages/About'),
  Contact: () => import('@/pages/Contact'),
  HowTo: () => import('@/pages/HowTo'),
  PrivacyPolicy: () => import('@/pages/PrivacyPolicy'),
  TermsOfService: () => import('@/pages/TermsOfService'),
  RefundPolicy: () => import('@/pages/RefundPolicy'),
  CookiePolicy: () => import('@/pages/CookiePolicy'),
  WithdrawalPolicy: () => import('@/pages/WithdrawalPolicy'),
  Imprint: () => import('@/pages/Imprint'),
  Auth: () => import('@/pages/Auth'),
  ForgotPassword: () => import('@/pages/ForgotPassword'),
  ResetPassword: () => import('@/pages/ResetPassword'),
  Account: () => import('@/pages/Account'),
  AccountOrderDetail: () => import('@/pages/account/OrderDetail'),
  Checkout: () => import('@/pages/Checkout'),
  CheckoutComplete: () => import('@/pages/CheckoutComplete'),
  CheckoutCancelled: () => import('@/pages/CheckoutCancelled'),
  Blog: () => import('@/pages/Blog'),
  BlogArticle: () => import('@/pages/BlogArticle'),
};

// All other pages — lazy loaded for smaller initial bundle
const Shop = React.lazy(lazyImports.Shop);
const Product = React.lazy(lazyImports.Product);
const Reviews = React.lazy(lazyImports.Reviews);
const About = React.lazy(lazyImports.About);
const Contact = React.lazy(lazyImports.Contact);
const HowTo = React.lazy(lazyImports.HowTo);
const PrivacyPolicy = React.lazy(lazyImports.PrivacyPolicy);
const TermsOfService = React.lazy(lazyImports.TermsOfService);
const RefundPolicy = React.lazy(lazyImports.RefundPolicy);
const CookiePolicy = React.lazy(lazyImports.CookiePolicy);
const WithdrawalPolicy = React.lazy(lazyImports.WithdrawalPolicy);
const Imprint = React.lazy(lazyImports.Imprint);
const Auth = React.lazy(lazyImports.Auth);
const ForgotPassword = React.lazy(lazyImports.ForgotPassword);
const ResetPassword = React.lazy(lazyImports.ResetPassword);
const Account = React.lazy(lazyImports.Account);
const AccountOrderDetail = React.lazy(lazyImports.AccountOrderDetail);
const Checkout = React.lazy(lazyImports.Checkout);
const CheckoutComplete = React.lazy(lazyImports.CheckoutComplete);
const CheckoutCancelled = React.lazy(lazyImports.CheckoutCancelled);
const Blog = React.lazy(lazyImports.Blog);
const BlogArticle = React.lazy(lazyImports.BlogArticle);

/** Prefetch map: route path → lazy import. Call prefetchRoute() on hover. */
const PREFETCH_MAP: Record<string, () => Promise<unknown>> = {
  '/shop': lazyImports.Shop,
  '/product': lazyImports.Product,
  '/blog': lazyImports.Blog,
  '/reviews': lazyImports.Reviews,
  '/about': lazyImports.About,
  '/contact': lazyImports.Contact,
  '/how-to': lazyImports.HowTo,
  '/auth': lazyImports.Auth,
  '/account': lazyImports.Account,
  '/checkout': lazyImports.Checkout,
};

const prefetched = new Set<string>();

export function prefetchRoute(path: string) {
  // Strip language prefix to match map keys
  const cleanPath = path.replace(/^\/(de|es|fr|pt|ru|it|sv|zh)/, '') || '/';
  if (prefetched.has(cleanPath)) return;
  // Also check if it's a product path (contains /id-card-generator/ or similar silo)
  const isProduct = /\/(id-card-generator|personalausweis-generator|generador-tarjetas|generateur-cartes|gerador-cartoes|generator-kart|generatore-carte|id-kort-generator|shenfenzheng)\//.test(cleanPath);
  const key = isProduct ? '/product' : cleanPath;
  if (prefetched.has(key)) return;
  const loader = PREFETCH_MAP[key];
  if (loader) {
    prefetched.add(key);
    loader();
  }
}

/** Eagerly prefetch the most common next-navigation chunks during idle time */
export function prefetchCriticalChunks() {
  const schedule = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 200);
  schedule(() => {
    prefetchRoute('/shop');
    prefetchRoute('/product');
  });
}

// Minimal spinner for lazy routes
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// Branded fallback for checkout route
const CheckoutFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      <p className="text-muted-foreground text-sm">Loading checkout...</p>
    </div>
  </div>
);

// Wrap lazy component in Suspense
function Lazy({ children }: { children: React.ReactNode }) {
  return (
    <LazyErrorBoundary>
      <Suspense fallback={<LazyFallback />}>{children}</Suspense>
    </LazyErrorBoundary>
  );
}

// Map page keys to components
const PAGE_COMPONENTS: Record<string, React.ComponentType> = {
  'home': Index,
  'shop': () => <Lazy><Shop /></Lazy>,
  'reviews': () => <Lazy><Reviews /></Lazy>,
  'about': () => <Lazy><About /></Lazy>,
  'contact': () => <Lazy><Contact /></Lazy>,
  'privacy': () => <Lazy><PrivacyPolicy /></Lazy>,
  'terms': () => <Lazy><TermsOfService /></Lazy>,
  'refund': () => <Lazy><RefundPolicy /></Lazy>,
  'withdrawal': () => <Lazy><WithdrawalPolicy /></Lazy>,
  'cookies': () => <Lazy><CookiePolicy /></Lazy>,
  'how-to': () => <Lazy><HowTo /></Lazy>,
  'imprint': () => <Lazy><Imprint /></Lazy>,
};

/**
 * Generate route elements for a specific language
 * Returns an array of Route elements that can be spread into Routes
 */
export function getLocalizedRoutes(langPrefix?: string): React.ReactElement[] {
  const routeLang = langPrefix || 'en';
  const langKey = `${routeLang}_slug` as keyof UrlTranslation;
  const routes: React.ReactElement[] = [];

  // Add localized page routes
  STATIC_TRANSLATIONS.forEach((translation) => {
    const Component = PAGE_COMPONENTS[translation.page_key];
    if (!Component) return;

    const slug = (translation[langKey] as string | null) || translation.en_slug;
    
    // Handle home page
    if (translation.page_key === 'home') {
      routes.push(<Route key={translation.page_key} index element={<Component />} />);
      return;
    }

    // Remove leading slash for nested routes
    const path = slug.startsWith('/') ? slug.slice(1) : slug;
    routes.push(<Route key={translation.page_key} path={path} element={<Component />} />);

    // Add English fallback redirect for non-English routes
    if (routeLang !== 'en' && slug !== translation.en_slug) {
      const enPath = translation.en_slug.startsWith('/') 
        ? translation.en_slug.slice(1) 
        : translation.en_slug;
      routes.push(
        <Route 
          key={`redirect-${translation.page_key}`} 
          path={enPath} 
          element={<Navigate to={`/${routeLang}/${path}`} replace />} 
        />
      );
    }
  });

  // Add product routes with localized silo paths
  const silo = PRODUCT_SILOS[routeLang] || PRODUCT_SILOS.en;
  routes.push(<Route key="product" path={`${silo}/:id`} element={<Lazy><Product /></Lazy>} />);
  // Also keep English silo as fallback for non-EN languages
  if (routeLang !== 'en') {
    routes.push(<Route key="product-en-fallback" path={`${PRODUCT_SILOS.en}/:id`} element={<Lazy><Product /></Lazy>} />);
  }
  routes.push(<Route key="checkout" path="checkout" element={<LazyErrorBoundary><Suspense fallback={<CheckoutFallback />}><Checkout /></Suspense></LazyErrorBoundary>} />);
  routes.push(<Route key="checkout-complete" path="checkout/complete" element={<Lazy><CheckoutComplete /></Lazy>} />);
  routes.push(<Route key="checkout-cancelled" path="checkout/cancelled" element={<Lazy><CheckoutCancelled /></Lazy>} />);

  // Blog routes with localized slug
  const blogTranslation = STATIC_TRANSLATIONS.find(t => t.page_key === 'blog');
  const blogSlug = blogTranslation
    ? ((blogTranslation[langKey] as string | null) || '/blog').replace(/^\//, '')
    : 'blog';
  routes.push(<Route key="blog" path={blogSlug} element={<Lazy><Blog /></Lazy>} />);
  routes.push(<Route key="blog-article" path={`${blogSlug}/:slug`} element={<Lazy><BlogArticle /></Lazy>} />);
  // English fallback for non-EN languages with different blog slug
  if (routeLang !== 'en' && blogSlug !== 'blog') {
    routes.push(<Route key="blog-redirect" path="blog" element={<Navigate to={`/${routeLang}/${blogSlug}`} replace />} />);
    routes.push(<Route key="blog-article-redirect" path="blog/:slug" element={<BlogArticleRedirect langPrefix={routeLang} blogSlug={blogSlug} />} />);
  }
  routes.push(<Route key="auth" path="auth" element={<Lazy><Auth /></Lazy>} />);
  routes.push(<Route key="forgot-password" path="forgot-password" element={<Lazy><ForgotPassword /></Lazy>} />);
  routes.push(<Route key="reset-password" path="reset-password" element={<Lazy><ResetPassword /></Lazy>} />);
  routes.push(<Route key="account" path="account" element={<Lazy><Account /></Lazy>} />);
  routes.push(<Route key="account-order" path="account/order/:id" element={<Lazy><AccountOrderDetail /></Lazy>} />);

  return routes;
}
